/**
 * MeterLoadProfile Component
 *
 * Self-contained component for load profile upload (CSV or AI extraction),
 * stats display, and expandable 24-hour bar chart. Renders inside each MeterCard.
 */

import React, { useState, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Alert,
    Collapse,
    CircularProgress,
    Chip,
    LinearProgress,
} from '@mui/material';
import {
    Upload,
    Delete,
    ExpandMore,
    ExpandLess,
    CalendarMonth,
} from '@mui/icons-material';
import { vortxColors, vortxTypography } from '../core/theme';
import { API_BASE_URL } from '../services/api';
import { useAuth } from '../auth/useAuth';
import { useExtraction } from '../extraction';
import LoadProfileChart from './LoadProfileChart';

/**
 * Format a number with commas and optional decimal places
 */
function formatNumber(value, decimals = 0) {
    if (value == null || isNaN(value)) return '--';
    return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

function MeterLoadProfile({
    meterId,
    loadProfile,
    onUpload,
    onRemove,
    projectId,
}) {
    const { currentUser } = useAuth();
    const fileInputRef = useRef(null);
    const pendingFileRef = useRef(null); // holds File for year re-submission
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [availableYears, setAvailableYears] = useState(null); // [{year, data_points, completeness_pct}]
    const [pendingUploadData, setPendingUploadData] = useState(null); // server response awaiting year pick

    const hasLoad = loadProfile?.loads_kw?.length === 8760;
    const metrics = loadProfile?.metrics || {};

    // Handle AI extraction completion (PDF/Excel via extraction pipeline)
    const handleExtractionComplete = useCallback((job) => {
        const extracted = job.extracted_data || job;
        const values = extracted?.values || [];

        if (values.length === 0) {
            setError('AI extraction returned no load data');
            return;
        }

        // Convert extracted values to 8760 loads_kw array
        let loadsKw;
        if (values.length === 8760) {
            loadsKw = values.map(v => typeof v === 'object' ? (v.kw || 0) : Number(v));
        } else if (values.length === 35040) {
            // 15-min data → average to hourly
            loadsKw = [];
            for (let i = 0; i < values.length; i += 4) {
                const chunk = values.slice(i, i + 4).map(v => typeof v === 'object' ? (v.kw || 0) : Number(v));
                loadsKw.push(chunk.reduce((a, b) => a + b, 0) / chunk.length);
            }
        } else {
            setError(`Expected 8760 hourly or 35040 quarter-hour values, got ${values.length}`);
            return;
        }

        const peakKw = loadsKw.reduce((max, v) => v > max ? v : max, -Infinity);
        const annualKwh = loadsKw.reduce((sum, v) => sum + v, 0);
        const loadFactor = peakKw > 0 ? (annualKwh / (peakKw * 8760)) * 100 : 0;

        onUpload(meterId, {
            loads_kw: loadsKw,
            year: extracted.year || new Date().getFullYear(),
            source: 'ai_extraction',
            file_name: extracted.site_name || 'AI Extracted Load Profile',
            uploaded_at: new Date().toISOString(),
            metrics: {
                peak_kw: peakKw,
                annual_kwh: annualKwh,
                load_factor: loadFactor,
            },
            decompositions: {},
        });
    }, [meterId, onUpload]);

    // Extraction hook for non-CSV files (PDF, Excel → Gemini pipeline)
    // Note: extraction.error is displayed separately in the render; don't also set component error
    const extraction = useExtraction({
        onComplete: handleExtractionComplete,
    });

    /**
     * Upload a file to the backend, optionally requesting a specific year.
     * year=0 requests the composite (merged) year.
     */
    const uploadFile = async (file, year = null) => {
        setError(null);
        setUploading(true);

        try {
            const token = currentUser ? await currentUser.getIdToken() : null;

            const formData = new FormData();
            formData.append('file', file);

            const headers = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            let url = `${API_BASE_URL}/api/site_dna/v1/load-profile/upload`;
            if (year != null) {
                url += `?year=${year}`;
            }

            const res = await fetch(url, {
                method: 'POST',
                headers,
                body: formData,
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || `Upload failed (${res.status})`);
            }

            const data = await res.json();

            if (!data.success || !data.loads_kw) {
                throw new Error(data.error || 'Invalid response from server');
            }

            return data;
        } catch (err) {
            console.error('Load profile upload failed:', err);
            setError(err.message || 'Upload failed');
            return null;
        } finally {
            setUploading(false);
        }
    };

    /**
     * Build processed data from server response and call onUpload
     */
    const finalizeUpload = (data, fileName) => {
        const loadsKw = data.loads_kw;
        const peakKw = Math.max(...loadsKw);
        const annualKwh = loadsKw.reduce((sum, v) => sum + v, 0);
        const loadFactor = peakKw > 0 ? (annualKwh / (peakKw * loadsKw.length)) * 100 : 0;

        const processedData = {
            loads_kw: loadsKw,
            year: data.year || new Date().getFullYear(),
            source: 'csv_upload',
            file_name: fileName,
            uploaded_at: new Date().toISOString(),
            metrics: {
                peak_kw: peakKw,
                annual_kwh: annualKwh,
                load_factor: loadFactor,
            },
            decompositions: {},
        };

        setAvailableYears(null);
        setPendingUploadData(null);
        pendingFileRef.current = null;
        onUpload(meterId, processedData);
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Reset file input so the same file can be re-selected
        event.target.value = '';

        // Clear any previous state
        setError(null);
        setAvailableYears(null);
        setPendingUploadData(null);
        extraction.reset();

        const ext = file.name.split('.').pop()?.toLowerCase();

        if (ext === 'csv') {
            // CSV → direct upload to Site DNA load-profile endpoint (fast, pandas)
            const data = await uploadFile(file);
            if (!data) return;

            const years = data.available_years || [];

            if (years.length > 1) {
                // Multiple years detected — show year picker
                pendingFileRef.current = file;
                setAvailableYears(years);
                setPendingUploadData(data);
            } else {
                // Single year or no datetime — proceed immediately
                finalizeUpload(data, file.name);
            }
        } else {
            // PDF, Excel → extraction pipeline (async, backend routes CSV to pandas too)
            extraction.upload(file, 'load_profile', { projectId });
        }
    };

    /**
     * User picked a year from the chips
     */
    const handleYearSelect = async (year) => {
        const file = pendingFileRef.current;
        if (!file) return;

        // If the user picked the already-selected year, just finalize
        if (pendingUploadData && pendingUploadData.year === year) {
            finalizeUpload(pendingUploadData, file.name);
            return;
        }

        // Re-upload with the chosen year
        const data = await uploadFile(file, year);
        if (!data) return;

        finalizeUpload(data, file.name);
    };

    const handleRemove = () => {
        setExpanded(false);
        setAvailableYears(null);
        setPendingUploadData(null);
        pendingFileRef.current = null;
        extraction.reset();
        onRemove(meterId);
    };

    const isExtracting = extraction.status === 'uploading' || extraction.status === 'processing';

    // ---- RENDER ----

    // CSV uploading state
    if (uploading) {
        return (
            <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={20} sx={{ color: vortxColors.gold }} />
                <Typography variant="body2" sx={{ color: vortxColors.stone }}>
                    Processing CSV...
                </Typography>
            </Box>
        );
    }

    // Extraction pipeline processing state (PDF/Excel)
    if (isExtracting) {
        return (
            <Box sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <CircularProgress size={20} sx={{ color: vortxColors.gold }} />
                    <Typography variant="body2" sx={{ color: vortxColors.stone }}>
                        {extraction.statusMessage || 'Extracting load profile...'}
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={extraction.progress}
                    sx={{
                        height: 4,
                        borderRadius: 2,
                        bgcolor: 'rgba(178, 140, 80, 0.15)',
                        '& .MuiLinearProgress-bar': {
                            bgcolor: vortxColors.gold,
                            borderRadius: 2,
                        },
                    }}
                />
                <Typography
                    variant="caption"
                    sx={{ color: vortxColors.stone, fontSize: '0.65rem', mt: 0.5, display: 'block', textAlign: 'right' }}
                >
                    {extraction.progress}%
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(63, 106, 100, 0.4)' }}>
            {/* Section Header */}
            <Typography
                variant="caption"
                sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    fontWeight: 600,
                    color: vortxColors.stone,
                    display: 'block',
                    mb: 1.5,
                }}
            >
                Load Profile
            </Typography>

            {/* Error */}
            {error && (
                <Alert
                    severity="error"
                    onClose={() => setError(null)}
                    sx={{ mb: 1.5, bgcolor: 'rgba(211, 47, 47, 0.1)', color: '#ef9a9a' }}
                >
                    {error}
                </Alert>
            )}

            {/* Hidden file input — accepts CSV, PDF, Excel */}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".csv,.pdf,.xlsx,.xls"
                style={{ display: 'none' }}
            />

            {/* Year picker — shown when multi-year CSV detected */}
            {availableYears && availableYears.length > 1 && !hasLoad && (() => {
                const yearChips = availableYears.filter(y => y.year !== 0);
                const composite = availableYears.find(y => y.year === 0);
                return (
                    <Box
                        sx={{
                            mb: 2,
                            p: 1.5,
                            bgcolor: 'rgba(178, 140, 80, 0.08)',
                            border: '1px solid rgba(178, 140, 80, 0.25)',
                            borderRadius: 1,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <CalendarMonth sx={{ fontSize: 16, color: vortxColors.gold }} />
                            <Typography variant="caption" sx={{ color: vortxColors.gold, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Multiple years detected — choose one
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            {yearChips.map((y) => {
                                const isSelected = pendingUploadData?.year === y.year;
                                return (
                                    <Chip
                                        key={y.year}
                                        label={`${y.year}  (${y.completeness_pct}%)`}
                                        onClick={() => handleYearSelect(y.year)}
                                        disabled={uploading}
                                        variant={isSelected ? 'filled' : 'outlined'}
                                        sx={{
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.75rem',
                                            borderColor: isSelected ? vortxColors.gold : vortxColors.teal,
                                            color: isSelected ? '#000' : vortxColors.white,
                                            bgcolor: isSelected ? vortxColors.gold : 'transparent',
                                            '&:hover': {
                                                bgcolor: isSelected
                                                    ? vortxColors.gold
                                                    : 'rgba(178, 140, 80, 0.15)',
                                                borderColor: vortxColors.gold,
                                            },
                                        }}
                                    />
                                );
                            })}
                            {/* Composite chip — merges all years to fill gaps */}
                            {composite && (
                                <>
                                    <Typography variant="caption" sx={{ color: vortxColors.stone, fontSize: '0.65rem', mx: 0.5 }}>
                                        or
                                    </Typography>
                                    <Chip
                                        label={`Composite  (${composite.completeness_pct}%)`}
                                        onClick={() => handleYearSelect(0)}
                                        disabled={uploading}
                                        variant="outlined"
                                        sx={{
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.75rem',
                                            borderColor: vortxColors.gold,
                                            borderStyle: 'dashed',
                                            color: vortxColors.gold,
                                            bgcolor: 'transparent',
                                            '&:hover': {
                                                bgcolor: 'rgba(178, 140, 80, 0.15)',
                                            },
                                        }}
                                    />
                                </>
                            )}
                        </Box>
                        <Typography variant="caption" sx={{ color: vortxColors.stone, fontSize: '0.65rem', mt: 0.5, display: 'block' }}>
                            Completeness % = hours with non-zero data. Composite merges all years to fill gaps.
                        </Typography>
                    </Box>
                );
            })()}

            {!hasLoad ? (
                /* No load profile — single upload button for all file types */
                <Box>
                    {extraction.error && (
                        <Alert
                            severity="error"
                            onClose={() => extraction.reset()}
                            sx={{ mb: 1.5, bgcolor: 'rgba(211, 47, 47, 0.1)', color: '#ef9a9a' }}
                        >
                            {extraction.error}
                        </Alert>
                    )}
                    <Button
                        variant="outlined"
                        startIcon={<Upload />}
                        onClick={() => fileInputRef.current?.click()}
                        size="small"
                        sx={{
                            borderColor: vortxColors.teal,
                            color: vortxColors.stone,
                            textTransform: 'none',
                            fontWeight: 500,
                            '&:hover': {
                                borderColor: vortxColors.gold,
                                color: vortxColors.gold,
                                bgcolor: 'rgba(178, 140, 80, 0.08)',
                            },
                        }}
                    >
                        Upload Load Profile
                    </Button>
                    <Typography variant="caption" sx={{ color: vortxColors.stone, fontSize: '0.6rem', ml: 1 }}>
                        CSV, PDF, or Excel
                    </Typography>
                </Box>
            ) : (
                /* Has load profile - show compact info + expand */
                <Box>
                    {/* File info alert with actions */}
                    <Alert
                        severity="success"
                        icon={false}
                        sx={{
                            bgcolor: 'rgba(178, 140, 80, 0.1)',
                            color: vortxColors.white,
                            border: '1px solid rgba(178, 140, 80, 0.3)',
                            py: 0.5,
                            '& .MuiAlert-message': { width: '100%' },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: vortxColors.white }}>
                                {loadProfile.file_name || 'Load profile data'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Button
                                    size="small"
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{
                                        color: vortxColors.gold,
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        minWidth: 'auto',
                                        '&:hover': { bgcolor: 'rgba(178, 140, 80, 0.15)' },
                                    }}
                                >
                                    Replace
                                </Button>
                                <IconButton
                                    size="small"
                                    onClick={handleRemove}
                                    sx={{
                                        color: vortxColors.stone,
                                        '&:hover': { color: '#d32f2f' },
                                    }}
                                >
                                    <Delete sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Box>
                        </Box>
                    </Alert>

                    {/* Stats row */}
                    <Box sx={{ display: 'flex', gap: 3, mt: 1.5, mb: 1 }}>
                        <Box>
                            <Typography variant="caption" sx={{ color: vortxColors.stone, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Peak
                            </Typography>
                            <Typography variant="body2" sx={{ color: vortxColors.white, fontWeight: 600, fontFamily: vortxTypography.mono, fontSize: '0.8rem' }}>
                                {formatNumber(metrics.peak_kw, 0)} kW
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: vortxColors.stone, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Annual
                            </Typography>
                            <Typography variant="body2" sx={{ color: vortxColors.white, fontWeight: 600, fontFamily: vortxTypography.mono, fontSize: '0.8rem' }}>
                                {formatNumber(metrics.annual_kwh, 0)} kWh
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: vortxColors.stone, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Load Factor
                            </Typography>
                            <Typography variant="body2" sx={{ color: vortxColors.white, fontWeight: 600, fontFamily: vortxTypography.mono, fontSize: '0.8rem' }}>
                                {formatNumber(metrics.load_factor, 1)}%
                            </Typography>
                        </Box>
                    </Box>

                    {/* View Chart expand button */}
                    <Button
                        size="small"
                        endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                        onClick={() => setExpanded(!expanded)}
                        sx={{
                            color: vortxColors.stone,
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            '&:hover': { color: vortxColors.gold },
                        }}
                    >
                        {expanded ? 'Hide Chart' : 'View Chart'}
                    </Button>

                    {/* Expanded chart area — full-year 8760 area chart */}
                    <Collapse in={expanded}>
                        <Box
                            sx={{
                                mt: 1,
                                height: 200,
                                bgcolor: 'rgba(0,0,0,0.2)',
                                border: '1px solid rgba(63, 106, 100, 0.3)',
                                borderRadius: 1,
                                p: 1,
                                pt: 2,
                            }}
                        >
                            <LoadProfileChart loads={loadProfile.loads_kw} />
                        </Box>
                    </Collapse>
                </Box>
            )}
        </Box>
    );
}

export default MeterLoadProfile;
